import styled from 'styled-components';

import React, { useCallback, useEffect, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router';
import FadeIn from 'react-fade-in';
import SubNavBar from './components/subnav';
import { ThemedModal } from 'common/components/cardStyles';
import {
  CurrencyConvertModal,
  CustomerReconcileModal,
  CustomerSettleModal,
  ReconcileRow,
  SettleRow,
} from './components/modalCards';
import { ProviderOutstandingTable } from './components/providerOustandingTable';
import { AccountOperatingTable } from './components/operatingTable';
import { AccountOwedTable } from './components/owedTable';
import { CustomersTable } from './components/customersTable';
import { CustomerOutstandingsTable } from './components/customerOutstandingsTable';
import { CustomerOutstanding } from 'common/state/customers/actions';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { customerSummariesRecipe } from 'common/state/customers/recipes';
import { defaultCustomerSummariesState } from 'common/state/customers/states';
import { doGetAllCustomerSummaries } from 'common/state/customers/thunkActions';

const BackgroundContainer = styled.div`
  flex: 1;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  margin: 1rem;
`;

export type ModalMode =
  | null
  | 'customerSettle'
  | 'customerReconcile'
  | 'currencyConvert';

function Reconcile() {
  const [selectedSettleRow, setSelectedSettleRow] = useState<SettleRow>(null);
  const [selectedReconcileRow, setSelectedReconcileRow] =
    useState<ReconcileRow>(null);
  const [selectedCurrencyConvertRow, setSelectedCurrencyConvertRow] =
    useState<CustomerOutstanding>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [summariesState, summariesDispatch] = useProducerWithThunks(
    customerSummariesRecipe,
    defaultCustomerSummariesState
  );

  const closeModal = useCallback(() => setModalMode(null), []);

  /**
   * We need to track if a modal successfully updated the database
   * If it did, we need to refresh the table
   * If the modal was a success, reset it to false for the next modal and trigger
   * A re-render for the Table routes
   */
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);

  useEffect(() => {
    summariesDispatch(doGetAllCustomerSummaries())
  }, [summariesDispatch]);

  useEffect(() => {
    if (shouldRefresh) {
      summariesDispatch(doGetAllCustomerSummaries())
    }
    setShouldRefresh(false);
  }, [summariesDispatch, shouldRefresh]);

  return (
    <BackgroundContainer>
      <SubNavBar />
      <Container>
        <Switch>
          <Route path="/reconcile/customers">
            <FadeIn>
              <CustomerOutstandingsTable
                setSelectedReconcileRow={setSelectedReconcileRow}
                setSelectedCurrencyConvertRow={setSelectedCurrencyConvertRow}
                setModalMode={setModalMode}
                shouldRefresh={shouldRefresh}
                customerSummaries={summariesState.summaries}
                isLoading={summariesState.pending}
                error={summariesState.error}
              />
              <CustomersTable
                setSelectedRow={setSelectedSettleRow}
                setModalMode={setModalMode}
                shouldRefresh={shouldRefresh}
                customerSummaries={summariesState.summaries}
                isLoading={summariesState.pending}
                error={summariesState.error}
              />
              {/* <WhitelabelAccountsTable /> */}
            </FadeIn>
          </Route>
          <Route path="/reconcile/providers">
            <FadeIn>
              <ProviderOutstandingTable />
              <br />
              <AccountOperatingTable />
              <br />
              <AccountOwedTable />
            </FadeIn>
          </Route>
          <Route render={() => <Redirect to="/reconcile/customers" />} />
        </Switch>
      </Container>
      <ThemedModal open={modalMode !== null}>
        {modalMode === 'customerSettle' && (
          <FadeIn>
            <CustomerSettleModal
              selectedRow={selectedSettleRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'customerReconcile' && (
          <FadeIn>
            <CustomerReconcileModal
              selectedRow={selectedReconcileRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'currencyConvert' && (
          <FadeIn>
            <CurrencyConvertModal
              selectedRow={selectedCurrencyConvertRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
      </ThemedModal>
    </BackgroundContainer>
  );
}

export default Reconcile;
